import { useEffect, useRef, useState } from 'react'
import { useAuth0 } from 'src/contexts/auth0'
import { useAPIContext } from 'src/contexts/api'
import { logError } from 'src/utils/Error'
import ndjsonStream from 'src/utils/stream/ndjson-stream'
import stringStream from 'src/utils/stream/string-stream'
import { DEFAULT_LOG_TAIL } from 'src/components/JobRow'
import IJob, { JobStatus } from 'src/models/IJob'
import ILog from 'src/models/ILog'
import { canDownloadLogs } from 'src/utils/jobStatus'

export interface IJobLogs {
  showLogs: (job: IJob) => void
  logModalProps: {
    show: boolean
    logArray: ILog[]
    job?: IJob
    hideLog: () => void
    downloadLog: (job: IJob) => Promise<void>
    taskId: any
  }
}

//
// Hosts the state and log fetch/poll behaviour behind LogModal, so any view
// can offer a "View logs" action on a job. Ported from the equivalent logic
// in Pipelines (modal state, download) and JobRow (tail fetch, 5s live-poll).
//
const useJobLogs = (): IJobLogs => {
  const { getTokenSilently } = useAuth0()
  const { catalogAPI } = useAPIContext()

  const [open, setOpen] = useState(false)
  const [job, setJob] = useState<IJob>()
  const [logsBySequence, setLogsBySequence] = useState<Record<string, ILog>>({})
  const [taskId, setTaskId] = useState<any>(null)
  // mirrors taskId for cleanup, and guards against an in-flight fetch
  // re-opening the modal after the user closes it
  const taskIdRef = useRef<any>(null)
  const activeRef = useRef(false)

  useEffect(() => () => {
    activeRef.current = false
    clearTimeout(taskIdRef.current)
  }, [])

  const renderLogs = (logs: ILog[], promiseId: any) => {
    taskIdRef.current = promiseId
    setLogsBySequence(prev => logs.reduce((obj, l) => ({ ...obj, [l.sequence]: l }), prev))
    setTaskId(promiseId)
  }

  const hideLog = () => {
    activeRef.current = false
    clearTimeout(taskIdRef.current)
    taskIdRef.current = null
    setOpen(false)
    setLogsBySequence({})
    setTaskId(null)
  }

  const downloadLog = async (job: IJob) => {
    if (!canDownloadLogs(job.status)) return
    await catalogAPI
      .get(job._links.logs.href)
      .then((resp: any) => {
        // a blob URL avoids the base64 memory overhead of a data: URI,
        // which matters now the full (unbounded) log is downloadable
        const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/plain' }))
        const link = document.createElementNS('http://www.w3.org/1999/xhtml', 'a') as HTMLAnchorElement
        link.href = url
        link.target = '_blank'
        link.download = `${job.id}.txt`
        link.click()
        URL.revokeObjectURL(url)
      })
      .catch((e: any) => {
        logError(e)
      })
  }

  // unlike DataRow, hosts of this hook don't re-poll the job list, so
  // refresh the job here to pick up a status change and end the poll loop
  const refreshJob = async (job: IJob): Promise<IJob> =>
    catalogAPI
      .get(job._links.self.href)
      .then((r: any) => ({ ...job, ...r.data }))
      .catch((e: any) => {
        logError(e)
        return job
      })

  const getLog = async (job: IJob, prevJob?: IJob, sequence?: string) => {
    if (!activeRef.current) return

    const logsHref = new URL(job._links.logs.href)

    setOpen(true)
    setJob(job)

    if (sequence !== undefined)
      logsHref.searchParams.set('sequence', sequence)
    else
      logsHref.searchParams.set('tail', String(DEFAULT_LOG_TAIL))

    try {
      const token = await getTokenSilently()

      await fetch(logsHref.toString(), {
        headers: {
          Accept: 'application/stream+json',
          Authorization: `Bearer ${token}`
        }
      })
        .then(async (response) => {
          if (response.status == 200) {
            if (typeof response.body !== 'undefined' && response.body != null) {
              return ndjsonStream(response.body)
            } else {
              const body = await response.text()
              return ndjsonStream(stringStream(body))
            }
          }
          return null
        })
        .then(async (jsonStream: ReadableStream<ILog> | null) => {
          if (!jsonStream)
            return

          const reader = jsonStream.getReader()
          const logs: ILog[] = []

          while (true) {
            const result = await reader.read()

            if (!result.done) {
              logs.push(result.value)
              continue
            }

            if (!activeRef.current)
              return

            const isJobActive = [JobStatus.Queued, JobStatus.Running].includes(job.status)
            const nextJob = isJobActive ? await refreshJob(job) : job
            const promiseId = (isJobActive || job.status !== prevJob?.status)
              ? setTimeout(getLog, 5000, nextJob, job, logs[logs.length - 1]?.sequence)
              : null

            return renderLogs(logs, promiseId)
          }
        })
        .catch(logError)
    } catch (e) {
      logError(e)
    }
  }

  const showLogs = (job: IJob) => {
    activeRef.current = true
    getLog(job)
  }

  return {
    showLogs,
    logModalProps: {
      show: open,
      logArray: Object.values(logsBySequence),
      job,
      hideLog,
      downloadLog,
      taskId
    }
  }
}

export default useJobLogs
